import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, Star, X, Search, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';

export const BinderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [binderName, setBinderName] = useState('Binder');
  const [cards, setCards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add card modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [userCards, setUserCards] = useState<any[]>([]);
  const [loadingUserCards, setLoadingUserCards] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [addingCardId, setAddingCardId] = useState<number | null>(null);

  // Delete confirm
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchBinderCards();
  }, [id]);

  const fetchBinderCards = async () => {
    if (!id) return;
    setLoading(true);
    try {
      // Fetch binder info
      const { data: binder, error: binderError } = await supabase
        .from('binders')
        .select('*')
        .eq('id', id)
        .single();

      if (binderError) throw binderError;
      if (binder) setBinderName(binder.name);

      // Fetch binder cards
      // We need to join binder_cards with cards
      const { data: binderCards, error: cardsError } = await supabase
        .from('binder_cards')
        .select(`
          id,
          card:cards (*)
        `)
        .eq('binder_id', id);

      if (cardsError) throw cardsError;
      setCards(binderCards || []);
    } catch (e) {
      console.error('Failed to fetch binder cards', e);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = async () => {
    setAddModalOpen(true);
    setSearchFilter('');
    setLoadingUserCards(true);
    try {
      const { data: cards, error } = await supabase
        .from('cards')
        .select('*')
        .eq('status', 'collection'); // Only show available cards? Or all? Let's show all for now or maybe just collection.

      if (error) throw error;
      setUserCards(cards || []);
    } catch (e) {
      console.error('Failed to fetch user cards', e);
    } finally {
      setLoadingUserCards(false);
    }
  };

  const addCardToBinder = async (cardId: number) => {
    if (!id) return;
    setAddingCardId(cardId);
    try {
      const { error } = await supabase
        .from('binder_cards')
        .insert({
          binder_id: id,
          card_id: cardId
        });

      if (error) throw error;

      setAddModalOpen(false);
      fetchBinderCards();
    } catch (e) {
      console.error('Failed to add card', e);
    } finally {
      setAddingCardId(null);
    }
  };

  const removeCard = async (binderCardId: number) => {
    if (!id) return;
    setDeletingId(binderCardId);
    try {
      const { error } = await supabase
        .from('binder_cards')
        .delete()
        .eq('id', binderCardId);

      if (error) throw error;

      setCards(prev => prev.filter(c => c.id !== binderCardId));
    } catch (e) {
      console.error('Failed to remove card', e);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredUserCards = userCards.filter(c =>
    !searchFilter || (c.name || '').toLowerCase().includes(searchFilter.toLowerCase())
  );

  // IDs of cards already in binder, to show "Already Added" badge
  const binderCardIds = new Set(cards.map((c: any) => c.card?.id));

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Background atmosphere */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-radial from-sage-500/5 to-transparent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative z-10 p-5 pt-16 pb-32 space-y-6">
        {/* Header */}
        <header className="sticky top-14 z-20 bg-obsidian/90 backdrop-blur-xl py-3 -mx-5 px-5 border-b border-white/[0.04]">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/collection')}
              className="p-2 card-premium rounded-xl hover:bg-white/5 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-cream-200/60" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-display font-semibold text-cream-50">{binderName}</h1>
              <p className="text-cream-200/40 text-xs mt-0.5">{cards.length} cards</p>
            </div>
            <button
              onClick={openAddModal}
              className="p-2.5 bg-sage-500/20 rounded-xl hover:bg-sage-500/30 transition-colors border border-sage-500/30"
            >
              <Plus className="w-4 h-4 text-sage-400" />
            </button>
          </div>
        </header>

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="flex gap-1.5">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-2 h-8 bg-sage-400/30 rounded-full animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
              ))}
            </div>
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-sage-500/10 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-sage-400/50" />
            </div>
            <h3 className="text-cream-100 font-semibold mb-2">Binder is empty</h3>
            <p className="text-cream-200/40 text-sm mb-6">Add cards from your collection to get started</p>
            <button
              onClick={openAddModal}
              className="px-6 py-3 bg-sage-500/20 text-sage-400 rounded-xl font-semibold border border-sage-500/30 hover:bg-sage-500/30 transition-colors"
            >
              Add Cards
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {cards.map((item: any, i: number) => {
              const card = item.card || {};
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="card-premium overflow-hidden group"
                >
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img
                      src={card.imageUrl && card.imageUrl.startsWith('http') ? card.imageUrl : 'https://placehold.co/300x400/1C1C1F/666?text=No+Image'}
                      alt={card.name || 'Card'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.src.includes('placehold.co')) {
                          target.src = 'https://placehold.co/300x400/1C1C1F/666?text=No+Image';
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                    <div className="absolute top-2 right-2 bg-obsidian/60 backdrop-blur-md px-2.5 py-1 rounded-lg text-[10px] font-semibold text-cream-100 border border-white/10 flex items-center gap-1">
                      <Star className="w-3 h-3 text-sage-400 fill-sage-400" />
                      {card.grade || 'Raw'}
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm truncate text-cream-100">{card.name || 'Unknown'}</h3>
                    <p className="text-[10px] text-cream-200/40 truncate">{card.setName || ''}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm font-semibold text-success">
                        {typeof card.price === 'number' ? `$${card.price.toFixed(2)}` : card.price || '$0.00'}
                      </span>
                      <button
                        onClick={() => removeCard(item.id)}
                        disabled={deletingId === item.id}
                        className="p-1.5 bg-white/5 rounded-lg hover:bg-danger/20 hover:text-danger transition-colors text-cream-200/40"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Card Modal */}
      <AnimatePresence>
        {addModalOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="bg-carbon border-t border-white/[0.06] rounded-t-3xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
            >
              <div className="p-6 pb-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-display font-semibold text-cream-50">Add Card to Binder</h3>
                  <button onClick={() => setAddModalOpen(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-cream-200/50 hover:text-cream-100 transition-colors">
                    <X size={18} />
                  </button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream-200/40" />
                  <input
                    type="text"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    placeholder="Filter your cards..."
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl py-3 pl-10 pr-4 text-sm text-cream-100 placeholder-cream-200/30 focus:outline-none focus:border-sage-400/50 transition-all"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 pt-2 space-y-2">
                {loadingUserCards ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-sage-400 animate-spin" />
                  </div>
                ) : filteredUserCards.length === 0 ? (
                  <p className="text-cream-200/40 text-sm text-center py-8">No cards in your collection yet. Scan or browse the catalog to add cards.</p>
                ) : (
                  filteredUserCards.map((card: any) => {
                    const alreadyIn = binderCardIds.has(card.id);
                    return (
                      <button
                        key={card.id}
                        onClick={() => !alreadyIn && addCardToBinder(card.id)}
                        disabled={alreadyIn || addingCardId === card.id}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors border ${alreadyIn
                          ? 'bg-sage-500/10 border-sage-500/20 opacity-60'
                          : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.05]'
                          }`}
                      >
                        <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-obsidian">
                          <img
                            src={(card.image_url || card.imageUrl) && (card.image_url || card.imageUrl)!.startsWith('http') ? (card.image_url || card.imageUrl)! : 'https://placehold.co/60x80/1C1C1F/666?text=Card'}
                            alt={card.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/60x80/1C1C1F/666?text=Card'; }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-cream-100 text-sm font-medium truncate">{card.name}</p>
                          <p className="text-cream-200/40 text-xs truncate">{card.set_name}</p>
                        </div>
                        <div className="flex-shrink-0">
                          {addingCardId === card.id ? (
                            <Loader2 className="w-4 h-4 text-sage-400 animate-spin" />
                          ) : alreadyIn ? (
                            <span className="text-sage-400 text-xs font-medium">Added</span>
                          ) : (
                            <Plus className="w-4 h-4 text-sage-400" />
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
